import { useMediaQuery } from '@mui/material';
import { RefObject, useCallback, useState } from 'react';
import { useSuppressSelection } from 'src/components/use-suppress-selection/use-suppress-selection';
import { LongPressEventType, useLongPress } from 'use-long-press';
import {
  REACTION_LONG_PRESS_MOVE_TOLERANCE_PX,
  REACTION_LONG_PRESS_MS,
} from './reactions.constants';

interface UseReactionPickerOptions {
  canReact: boolean;
  /** What a long press anchors the picker to. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Areas that handle their own presses, e.g. reaction chips or nested reactable items. */
  ignoredRefs: RefObject<HTMLElement | null>[];
}

/**
 * Open state of a message's reaction picker. It opens from the add-reaction buttons or, on touch
 * devices, a long press on the message, and holds off text selection while it is open.
 */
export const useReactionPicker = ({
  canReact,
  anchorRef,
  ignoredRefs,
}: UseReactionPickerOptions) => {
  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const close = useCallback(() => setAnchorEl(null), []);
  const handleAddReactionClick = useCallback(
    (event: { currentTarget: HTMLElement }) => setAnchorEl(event.currentTarget),
    [],
  );

  useSuppressSelection(isOpen && isTouchDevice);
  const bindLongPress = useLongPress(
    canReact && isTouchDevice ? () => setAnchorEl(anchorRef.current) : null,
    {
      threshold: REACTION_LONG_PRESS_MS,
      detect: LongPressEventType.Touch,
      cancelOnMovement: REACTION_LONG_PRESS_MOVE_TOLERANCE_PX,
      filterEvents: (event) =>
        !ignoredRefs.some(
          (ref) => event.target instanceof Node && ref.current?.contains(event.target),
        ),
    },
  );

  return {
    anchorEl,
    isOpen,
    openAt: setAnchorEl,
    close,
    handleAddReactionClick,
    longPressHandlers: bindLongPress,
  };
};

export type ReactionPickerState = ReturnType<typeof useReactionPicker>;
